import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ShieldCheck, Lock } from "lucide-react";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [show, setShow] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [label, setLabel] = useState("");
  const [perms, setPerms] = useState([]);
  const [adminSecs, setAdminSecs] = useState([]);
  const [builtin, setBuiltin] = useState(false);

  const load = useCallback(() => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/meta").then((r) => setPages(r.data.pages || [])).catch(() => {}); }, []);

  const openNew = () => { setEditKey(null); setLabel(""); setPerms([]); setAdminSecs([]); setBuiltin(false); setShow(true); };
  const openEdit = (r) => { setEditKey(r.key); setLabel(r.label); setPerms(r.permissions || []); setAdminSecs(r.admin_sections || []); setBuiltin(!!r.builtin); setShow(true); };
  const toggle = (p) => setPerms((cur) => {
    if (cur.includes(p)) { setAdminSecs((a) => a.filter((x) => x !== p)); return cur.filter((x) => x !== p); }
    return [...cur, p];
  });
  const toggleAdmin = (p) => setAdminSecs((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { label, permissions: perms, admin_sections: adminSecs };
      if (editKey) await api.put(`/roles/${editKey}`, body);
      else await api.post("/roles", body);
      toast.success(editKey ? "Role updated" : "Role created");
      setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (key) => {
    if (!window.confirm("Delete this role?")) return;
    try { await api.delete(`/roles/${key}`); toast.success("Role deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const pageLabel = (k) => pages.find((p) => p.key === k)?.label || k;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Roles &amp; Permissions</h1>
          <p className="text-sm text-slate-500 mt-1">Create roles and control which sections each role can access in their login</p>
        </div>
        <button data-testid="add-role-button" onClick={openNew} className={btnPrimary}><Plus size={16} /> New Role</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((r) => (
          <div key={r.key} data-testid={`role-card-${r.key}`} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><ShieldCheck size={20} /></div>
                <div>
                  <div className="font-bold text-slate-900 font-heading flex items-center gap-1.5">{r.label}{r.builtin && <Lock size={12} className="text-slate-400" />}</div>
                  <div className="text-xs text-slate-400">{r.key === "admin" ? "Full access" : `${(r.permissions || []).length} section(s)`}</div>
                </div>
              </div>
              <div className="flex gap-1">
                {r.key !== "admin" && <button data-testid={`edit-role-${r.key}`} onClick={() => openEdit(r)} className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={14} /></button>}
                {!r.builtin && <button data-testid={`delete-role-${r.key}`} onClick={() => del(r.key)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {r.key === "admin" ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-900 text-white">All sections</span>
                : (r.permissions || []).map((p) => <span key={p} className={`text-[10px] px-2 py-0.5 rounded-full ${(r.admin_sections || []).includes(p) ? "bg-emerald-100 text-emerald-700 font-semibold" : "bg-slate-100 text-slate-600"}`}>{pageLabel(p)}{(r.admin_sections || []).includes(p) ? " ·admin" : ""}</span>)}
            </div>
          </div>
        ))}
      </div>

      <Modal open={show} onClose={() => setShow(false)} title={editKey ? "Edit Role" : "New Role"} testId="role-modal">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Role Name *</label>
            <input data-testid="role-label-input" required disabled={builtin} value={label} onChange={(e) => setLabel(e.target.value)} className={`${inputCls} disabled:bg-slate-50`} placeholder="e.g. Operations Executive" />
            {builtin && <p className="text-xs text-slate-400 mt-1">Built-in role name can't be changed, but its access can.</p>}
          </div>
          <div>
            <label className={labelCls}>Accessible Sections</label>
            <p className="text-xs text-slate-400 mb-2 -mt-1">Tick a section to grant view access. Toggle <span className="font-semibold text-emerald-600">Admin</span> to allow create/edit/delete within that section only.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {pages.map((p) => (
                <div key={p.key} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border ${perms.includes(p.key) ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50/50"}`}>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer flex-1">
                    <input data-testid={`role-perm-${p.key}`} type="checkbox" checked={perms.includes(p.key)} onChange={() => toggle(p.key)} className="w-4 h-4 accent-emerald-600" />
                    {p.label}
                  </label>
                  {p.admin && perms.includes(p.key) && (
                    <button type="button" data-testid={`role-admin-${p.key}`} onClick={() => toggleAdmin(p.key)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${adminSecs.includes(p.key) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-300 hover:border-emerald-400"}`}>
                      {adminSecs.includes(p.key) ? "Admin ✓" : "Admin"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="submit-role-button" type="submit" className={btnPrimary}>{editKey ? "Save" : "Create Role"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
